import {
  redirect,
  Navigate,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router';
import App from '../App';
import HomePage from '../pages/home';
import ChatPage from '@/pages/chat';
import LoginPage from '../pages/login';
import NotFoundPage from '../pages/not-found';
import ErrorPage from '@/pages/error';
import { AuthProvider } from '@/lib/services/auth.service';
import CatalogPage from '@/pages/catalog';
import { fetchAppData } from '@/lib/app-data';
import { PolicyModel } from '@/lib/models/policy.model';

const rootRoute = createRootRoute({
  component: App,
});

const requireAuth = () => {
  if (!AuthProvider.user) {
    throw redirect({
      to: '/login',
    });
  }
};

const excludeAuth = () => {
  if (AuthProvider.user) {
    throw redirect({
      to: '/apps',
    });
  }
};

const requireChat = async () => {
  requireAuth();
  const userPolicy = await fetchAppData<PolicyModel>('portal-policy');

  if (!userPolicy?.portal?.enabled) {
    throw redirect({
      to: '/apps',
      replace: true,
    });
  }
};

type ChatPageSearch = {
  assistant_id?: string;
};

const routes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    beforeLoad: async () => {
      requireAuth();
      const userPolicy = await fetchAppData<PolicyModel>('portal-policy');
      throw redirect({
        to: userPolicy?.portal?.enabled ? '/chat' : '/apps',
        replace: true,
      });
    },
    component: () => null,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/apps',
    component: HomePage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/chat',
    component: ChatPage,
    beforeLoad: requireChat,
    validateSearch: ({ assistant_id }: Record<string, unknown>): ChatPageSearch => {
      if (!assistant_id) {
        return {};
      }

      return {
        assistant_id: assistant_id ? String(assistant_id) : '',
      };
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/catalog',
    component: CatalogPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/chat/$threadId',
    component: ChatPage,
    beforeLoad: requireChat,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: LoginPage,
    beforeLoad: excludeAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/error',
    component: ErrorPage,
  }),

  // old /home routes
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/home/$threadId',
    beforeLoad: ({ params }) => {
      throw redirect({
        to: '/chat/$threadId',
        params: {
          threadId: params.threadId,
        },
        replace: true,
      });
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/home',
    component: () => <Navigate to="/chat" />,
  }),

  // 404
  createRoute({
    getParentRoute: () => rootRoute,
    path: '*',
    component: NotFoundPage,
  }),
];

const routeTree = rootRoute.addChildren(routes);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default router;
